/**
 * Post-login redirect: query `redirect`, session backup, then educator app via
 * REACT_APP_EDUCATOR_URL for staff-approved Mentors. Uses `window.location.assign`
 * so navigation runs synchronously from the sign-in saga before React effects.
 *
 * Cross-origin: append `#auth=` handoff (see educator `AuthHandoff.tsx`).
 */
import {
  getJWTFromLocalStorage,
  getREFRESHFromLocalStorage,
  getUSERFromLocalStorage,
} from "./localStorage";
import { isAuthRevoked } from "./authSession";

/** Survives cases where `useSearchParams()` is empty when a passive effect runs. */
export const SIGNIN_REDIRECT_STORAGE_KEY = "career_scoops_signin_redirect";

function utf8ToBase64(str) {
  return window.btoa(unescape(encodeURIComponent(str)));
}

function withCrossOriginAuthHandoff(absoluteUrl) {
  if (isAuthRevoked()) {
    return absoluteUrl.split("#")[0];
  }
  const access = getJWTFromLocalStorage();
  const refresh = getREFRESHFromLocalStorage();
  const user = getUSERFromLocalStorage();
  if (!access) {
    return absoluteUrl;
  }
  try {
    const payload = { access, refresh: refresh || "", user: user ?? null };
    const json = JSON.stringify(payload);
    const b64 = utf8ToBase64(json);
    const encoded = encodeURIComponent(b64);
    const dest = new URL(absoluteUrl);
    if (dest.hash && dest.hash.length > 1) {
      dest.hash = `${dest.hash}&auth=${encoded}`;
    } else {
      dest.hash = `auth=${encoded}`;
    }
    return dest.toString();
  } catch {
    return absoluteUrl;
  }
}

function readRedirectFromWindow() {
  try {
    return new URLSearchParams(window.location.search).get("redirect")?.trim() || "";
  } catch {
    return "";
  }
}

function readRedirectFromSessionStorage() {
  try {
    return sessionStorage.getItem(SIGNIN_REDIRECT_STORAGE_KEY)?.trim() || "";
  } catch {
    return "";
  }
}

function clearStoredRedirect() {
  try {
    sessionStorage.removeItem(SIGNIN_REDIRECT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Clear persisted post-login redirect (e.g. after sign-out). */
export function clearSignInRedirectSessionStorage() {
  clearStoredRedirect();
}

/**
 * Persist `?redirect=` from the current URL. Clears stored value when the address bar has no `redirect`.
 */
export function syncSignInRedirectSessionStorage() {
  try {
    const fromUrl = readRedirectFromWindow();
    if (fromUrl) {
      sessionStorage.setItem(SIGNIN_REDIRECT_STORAGE_KEY, fromUrl);
    } else {
      sessionStorage.removeItem(SIGNIN_REDIRECT_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

/**
 * @param {string | null | undefined} redirectFromRouter — optional hint from `useSearchParams`
 */
export function resolvePostSignInRedirect(redirectFromRouter) {
  const fromRouter = redirectFromRouter && String(redirectFromRouter).trim();
  return fromRouter || readRedirectFromWindow() || readRedirectFromSessionStorage();
}

/** True when user may use the educator dashboard (Mentor + staff, or Admin). */
export function canAccessEducatorDashboard(user) {
  if (!user) return false;
  if (user.role === "Admin") return true;
  return user.role === "Mentor" && Boolean(user.is_staff);
}

function getEducatorAppOrigin() {
  const raw = process.env.REACT_APP_EDUCATOR_URL;
  if (!raw || typeof raw !== "string") return null;
  const base = raw.replace(/\/$/, "");
  const withScheme = /^https?:\/\//i.test(base) ? base : `http://${base}`;
  try {
    return new URL(withScheme).origin;
  } catch {
    return null;
  }
}

function isEducatorAppUrl(url) {
  const educatorOrigin = getEducatorAppOrigin();
  if (!educatorOrigin) return false;
  try {
    return new URL(url).origin === educatorOrigin;
  } catch {
    return false;
  }
}

function userForRedirectGate(loginResponse) {
  return loginResponse?.user ?? getUSERFromLocalStorage();
}

/** Staff-approved mentors only — Admins use the educator URL directly if needed. */
function educatorDashboardFallbackFromUser(user) {
  if (user?.role !== "Mentor" || !user?.is_staff) return "";
  const raw = process.env.REACT_APP_EDUCATOR_URL;
  if (!raw || typeof raw !== "string") return "";
  const base = raw.replace(/\/$/, "");
  const withScheme = /^https?:\/\//i.test(base) ? base : `http://${base}`;
  try {
    const u = new URL(withScheme);
    return `${u.origin}/dashboard`;
  } catch {
    return `${withScheme}/dashboard`;
  }
}

function resolveFullRedirectTarget(redirectHint, loginResponse) {
  const user = userForRedirectGate(loginResponse);
  const fromQuery = resolvePostSignInRedirect(redirectHint);
  if (fromQuery) {
    if (isEducatorAppUrl(fromQuery) && !canAccessEducatorDashboard(user)) {
      return "";
    }
    return fromQuery;
  }
  if (loginResponse?.user) {
    const fb = educatorDashboardFallbackFromUser(loginResponse.user);
    if (fb) return fb;
  }
  return "";
}

function assignPostLogin(target) {
  const origin = window.location.origin;
  if (!target?.trim()) {
    window.location.assign(`${origin}/profile`);
    return;
  }
  const t = target.trim();
  if (/^https?:\/\//i.test(t)) {
    try {
      const dest = new URL(t);
      if (dest.origin === origin) {
        window.location.assign(`${dest.pathname}${dest.search}${dest.hash}`);
        return;
      }
      window.location.assign(withCrossOriginAuthHandoff(t));
      return;
    } catch {
      window.location.assign(t);
      return;
    }
  }
  if (t.startsWith("/")) {
    window.location.assign(`${origin}${t}`);
    return;
  }
  window.location.assign(`${origin}/${t}`);
}

/**
 * @param {{ redirectHint?: string | null; loginResponse?: { user?: { role?: string; is_staff?: boolean } } }} | undefined} options
 */
export function applyPostSignInRedirect(options) {
  if (typeof window === "undefined") return;
  if (isAuthRevoked()) return;
  const { redirectHint, loginResponse } = options || {};
  const target = resolveFullRedirectTarget(redirectHint, loginResponse);
  clearStoredRedirect();
  assignPostLogin(target);
}

/** @deprecated Prefer `applyPostSignInRedirect`; kept for any external imports. */
export function followSignInRedirect(navigate, redirectFromRouter) {
  void navigate;
  applyPostSignInRedirect({ redirectHint: redirectFromRouter });
}
